/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

// TODO: set the fl-player version, ppid
export const DEFAULT_PAL_CONFIGURATION = {
  adWillPlayMuted: false,
  continuousPlayback: false,
  iconsSupported: false,
  playerType: 'FL Player',
  videoHeight: 480,
  videoWidth: 640,
};

/**
 * The NonceRequest is used to store information about the request for a nonce.
 */
export interface PALConfiguration {
  /**
   * Set to true if the ad will be auto played without waiting for user interaction. Set to false if the ad will
   * wait for user interaction before being played.
   */
  adWillAutoPlay?: boolean;

  /**
   * Set to true if the ad will be played while muted. Set to false if the ad will play unmuted. Default value
   * is false.
   */
  adWillPlayMuted?: boolean;

  /**
   * Set to true if the player intends to continuously play the content videos one after another similar to TV
   * broadcast or video playlist. Set to false otherwise. Default value is false.
   */
  continuousPlayback?: boolean;

  /**
   * The description URL of the content during which the ad will be played. Any description URL string longer
   * than 500 characters will be ignored and excluded from the nonce.
   */
  descriptionUrl: string;

  /**
   * Whether VAST icons are supported by the video player. Default value is false.
   */
  iconsSupported?: boolean;

  /**
   * The length limit of the generated nonce. The loadNonceManager promise will be rejected if the nonce is longer
   * than this limit. Defaults to the largest possible integer. Choosing a shorter length limit may cause various
   * targeting properties to be excluded from the nonce.
   */
  nonceLengthLimit?: number;

  /**
   * The name of the partner integrating OMID measurement. Any partner name string longer than 200 characters will
   * be ignored and excluded from the nonce.
   *
   * This must match the name supplied to the OM SDK for the ad session. For further details, see the OM SDK
   * Onboarding Guide (https://iabtechlab.com/omsdk/docs/onboarding).
   */
  omidPartnerName?: string;

  /**
   * The version of the partner integrating OMID measurement. Any partner version string longer than 200 characters
   * will be ignored and excluded from the nonce.
   *
   * This must match the string supplied to the OM SDK for the ad session.
   */
  omidPartnerVersion?: string;

  /**
   * The version of OMID that the player responsible for ad playback integrates with. Any OMID version string longer
   * than 200 characters will be ignored and excluded from the nonce.
   *
   * This must be set to the version provided by the OM SDK's getVersion() method. For further details, see the
   * OMID API docs (https://iabtechlab.com/omsdk/docs/api/1.2).
   */
  omidVersion?: string;

  /**
   * Sets the partner provided player type. This should be used to specify the name of the partner player. Any
   * player type string longer than 200 characters will be ignored and excluded from the nonce. Default value
   * is FL Player.
   */
  playerType?: string;

  /**
   * Sets the partner provided player version. This should be used to specify the version of the partner player.
   * Any player version string longer than 200 characters will be ignored and excluded from the nonce. Default value
   * is the FL Player version.
   */
  playerVersion: string;

  /**
   * The publisher provided ID. Any PPID longer than 200 characters will be ignored and excluded from the nonce.
   */
  ppid: string;

  /**
   * The height of the ad video element. Default value is 480.
   */
  videoHeight?: number;

  /**
   * The width of the ad video element. Default value is 640.
   */
  videoWidth?: number;
}
